/** Standard form footer with cancel and submit buttons. */
'use client';

import { clsx } from 'clsx';
import Button from './Button';
import { useModalTestId } from './Modal';

/**
 * Props for the FormFooter component.
 */
export interface FormFooterProps {
  /** Callback when cancel is clicked */
  onCancel: () => void;
  /** Callback when submit is clicked (use when not inside a <form>) */
  onSubmit?: () => void;
  /**
   * The id of the `<form>` this footer submits, for a footer rendered OUTSIDE
   * that form — a `Modal`'s `footer` slot sits beside the body, not inside it
   * (BL-2197). Save becomes `type="submit" form={form}`: the form's own submit
   * button, so Enter in a field submits it and the form's `onSubmit` receives
   * a real submit event. Leave `onSubmit` unset when you pass this.
   */
  form?: string;
  /** Label for the submit button (default: "Save") */
  submitLabel?: string;
  /**
   * The submit button's accessible name, when its visible label is shared with
   * another button on the screen — e.g. a modal's "Add bidder" submit beside the
   * page's "Add bidder" button that opened it (BL-1961). Start it with the
   * visible label so voice-control users can still say what they see. Omitted,
   * the name is the visible label.
   */
  submitAriaLabel?: string;
  /** Label for the cancel button (default: "Cancel") */
  cancelLabel?: string;
  /** Show loading spinner on submit button */
  isSubmitting?: boolean;
  /** Disable the submit button */
  isDisabled?: boolean;
  /**
   * Visual weight of the submit button. `destructive` for a footer whose submit
   * commits something the operator can't quietly undo — a renumber across a
   * live sale, a delete. Defaults to the primary fill, so existing consumers
   * are unchanged.
   */
  submitVariant?: 'primary' | 'destructive';
  /**
   * An action that belongs to the record rather than to the form — Archive,
   * Delete, "Duplicate this". Rendered hard-left, pushed away from Cancel/Save
   * so it can't be hit by someone aiming for Cancel. Omitted, the row stays
   * `justify-end` exactly as before, so no existing consumer moves.
   */
  leadingAction?: React.ReactNode;
  /**
   * Draw only the cancel button — a window with nothing to submit, e.g. one
   * showing why its action is refused (BUG-2041). Defaults to `true`, so no
   * existing consumer changes.
   */
  showSubmit?: boolean;
  /** Additional CSS classes */
  className?: string;
  /**
   * Automation namespace (BL-1867): `<testId>-save` on the submit button and
   * `<testId>-cancel` on Cancel. Defaults to the enclosing `Modal`'s `testId`,
   * so a named modal's footer needs no prop; with neither, nothing is emitted.
   */
  testId?: string;
}

/**
 * Renders a border-separated footer row with cancel and submit buttons.
 * Used at the bottom of modal forms and settings panels.
 * @param props - Callbacks, labels, and loading/disabled state.
 * @returns A flex row with cancel (outline) and submit (primary) buttons.
 */
export default function FormFooter({
  onCancel,
  onSubmit,
  form,
  submitLabel = 'Save',
  submitAriaLabel,
  cancelLabel = 'Cancel',
  isSubmitting = false,
  isDisabled = false,
  submitVariant = 'primary',
  leadingAction,
  showSubmit = true,
  className,
  testId,
}: FormFooterProps) {
  // Read unconditionally (rule of hooks); an explicit testId wins.
  const modalTestId = useModalTestId();
  const ns = testId ?? modalTestId;
  return (
    <div
      className={clsx(
        'flex flex-wrap items-center gap-density-control pt-4 mt-6 border-t border-gray-200',
        leadingAction ? 'justify-between' : 'justify-end',
        className,
      )}
    >
      {leadingAction ? <div className="flex items-center gap-density-cluster">{leadingAction}</div> : null}
      <div className="flex flex-wrap items-center justify-end gap-density-control">
      {/* `type="button"` is load-bearing, not decoration: HTML defaults a
          <button> inside a <form> to type="submit", and Button sets no default.
          Without this, Cancel SUBMITS the surrounding form — firing the save it
          is supposed to abandon. Latent in every in-form consumer until BL-986's
          "Cancel never writes" test caught it. */}
      <Button
        type="button"
        variant="outline"
        onClick={onCancel}
        disabled={isSubmitting}
        data-testid={ns ? `${ns}-cancel` : undefined}
      >
        {cancelLabel}
      </Button>
      {showSubmit ? (
      <Button
        type={onSubmit ? 'button' : 'submit'}
        form={onSubmit ? undefined : form}
        variant={submitVariant}
        onClick={onSubmit}
        isLoading={isSubmitting}
        disabled={isDisabled}
        aria-label={submitAriaLabel}
        data-testid={ns ? `${ns}-save` : undefined}
      >
        {submitLabel}
      </Button>
      ) : null}
      </div>
    </div>
  );
}
